/**
 * 搜索上下文管理
 * 职责：管理搜索相关的上下文数据
 */

import { contextManager } from "./ContextManager";

export interface SearchContextData {
  search_word: string;
  search_word_type?: string;
  search_result_number?: number;
  search_type?: string;
  filter_ids?: string[];
  original_search_word?: string;
  top_label_list?: string[];
  top_label_depth?: number;
  filter_one?: string;
}

const SEARCH_CONTEXT_KEY = "search";
const SEARCH_CONTEXT_TTL = 30 * 60 * 1000; // 30分钟

export class SearchContext {
  /**
   * 创建搜索上下文
   */
  static create(
    data: SearchContextData,
    source: "user_search" | "url_param" | "cross_page" = "user_search"
  ): void {
    // ⚠️ 重要：如果 SearchContext 已存在，合并数据而不是覆盖
    // 这样可以确保切换Tab时，已有的 search_result_number 等字段不会被清空
    const existingContext = contextManager.get(SEARCH_CONTEXT_KEY);
    if (existingContext) {
      // 合并数据：新数据覆盖旧数据，但保留旧数据中不在新数据中的字段
      const existingData = existingContext.data as SearchContextData;
      const mergedData: SearchContextData = {
        ...existingData,
        ...data,
      };
      contextManager.register(SEARCH_CONTEXT_KEY, {
        type: "search",
        data: mergedData,
        metadata: {
          createdAt: existingContext.metadata.createdAt, // 保留原始创建时间
          ttl: SEARCH_CONTEXT_TTL,
          scope: "global",
          sessionId: existingContext.metadata.sessionId, // 保留原始 sessionId
        },
      });

      if (process.env.NODE_ENV === "development") {
        console.log("[SearchContext] Merged:", { data, source, mergedData });
      }
    } else {
      // 如果 SearchContext 不存在，创建新的
      contextManager.register(SEARCH_CONTEXT_KEY, {
        type: "search",
        data,
        metadata: {
          createdAt: Date.now(),
          ttl: SEARCH_CONTEXT_TTL,
          scope: "global",
          sessionId: `search_${Date.now()}_${Math.random()
            .toString(36)
            .slice(2)}`,
        },
      });

      if (process.env.NODE_ENV === "development") {
        console.log("[SearchContext] Created:", { data, source });
      }
    }
  }

  /**
   * 更新搜索上下文
   */
  static update(data: Partial<SearchContextData>): void {
    contextManager.merge(SEARCH_CONTEXT_KEY, data);

    if (process.env.NODE_ENV === "development") {
      console.log("[SearchContext] Updated:", data);
    }
  }

  /**
   * 获取搜索上下文
   */
  static get(): SearchContextData | null {
    const context = contextManager.get(SEARCH_CONTEXT_KEY);
    return context ? (context.data as SearchContextData) : null;
  }

  /**
   * 清除搜索上下文
   */
  static clear(): void {
    contextManager.clear(SEARCH_CONTEXT_KEY);

    if (process.env.NODE_ENV === "development") {
      console.log("[SearchContext] Cleared");
    }
  }

  /**
   * 序列化为 URL 参数
   */
  static toURLParams(): string {
    const data = this.get();
    if (!data) return "";

    const params: string[] = [];

    if (data.search_word) {
      params.push(`search_word=${encodeURIComponent(data.search_word)}`);
    }
    if (data.search_word_type) {
      params.push(
        `search_word_type=${encodeURIComponent(data.search_word_type)}`
      );
    }
    if (data.search_result_number !== undefined) {
      params.push(`search_result_number=${data.search_result_number}`);
    }
    if (data.search_type) {
      params.push(`search_type=${encodeURIComponent(data.search_type)}`);
    }
    if (data.filter_ids && data.filter_ids.length > 0) {
      params.push(
        `filter_ids=${encodeURIComponent(data.filter_ids.join(","))}`
      );
    }
    if (data.original_search_word) {
      params.push(
        `original_search_word=${encodeURIComponent(data.original_search_word)}`
      );
    }
    if (data.top_label_list && data.top_label_list.length > 0) {
      params.push(
        `top_label_list=${encodeURIComponent(data.top_label_list.join(","))}`
      );
    }
    if (data.top_label_depth !== undefined) {
      params.push(`top_label_depth=${data.top_label_depth}`);
    }
    if (data.filter_one) {
      params.push(`filter_one=${encodeURIComponent(data.filter_one)}`);
    }

    // 添加标记，表示这是序列化的搜索上下文
    params.push("_searchContext=true");

    return params.join("&");
  }

  /**
   * 从 URL 参数解析
   */
  static fromURLParams(search: string): SearchContextData | null {
    const params = new URLSearchParams(search);

    // 检查是否包含搜索上下文标记
    if (
      !params.get("_searchContext") &&
      !params.get("q") &&
      !params.get("search_word") &&
      !params.get("searchWord")
    ) {
      return null;
    }

    const data: Partial<SearchContextData> = {};

    // 优先使用 search_word，其次使用 searchWord，最后使用 q
    const searchWord =
      params.get("search_word") || params.get("searchWord") || params.get("q");
    if (searchWord) {
      data.search_word = searchWord;
    }

    const searchWordType = params.get("search_word_type");
    if (searchWordType) {
      data.search_word_type = searchWordType;
    }

    const searchResultNumber = params.get("search_result_number");
    if (searchResultNumber) {
      data.search_result_number = parseInt(searchResultNumber, 10);
    }

    const searchType = params.get("search_type");
    if (searchType) {
      data.search_type = searchType;
    }

    const filterIds = params.get("filter_ids");
    if (filterIds) {
      data.filter_ids = filterIds.split(",");
    }

    const originalSearchWord = params.get("original_search_word");
    if (originalSearchWord) {
      data.original_search_word = originalSearchWord;
    }

    const topLabelList = params.get("top_label_list");
    if (topLabelList) {
      data.top_label_list = topLabelList.split(",");
    }

    const topLabelDepth = params.get("top_label_depth");
    if (topLabelDepth) {
      data.top_label_depth = parseInt(topLabelDepth, 10);
    }

    const filterOne = params.get("filter_one");
    if (filterOne) {
      data.filter_one = filterOne;
    }

    // 只有包含 search_word 才创建上下文
    if (data.search_word) {
      this.create(data as SearchContextData, "url_param");
      return data as SearchContextData;
    }

    return null;
  }

  /**
   * 订阅搜索上下文变化
   */
  static subscribe(
    listener: (data: SearchContextData | null) => void
  ): () => void {
    return contextManager.subscribe(SEARCH_CONTEXT_KEY, (context) => {
      listener(context?.data as SearchContextData | null);
    });
  }

  /**
   * 从当前 URL 同步搜索上下文
   * 自动解析 URL 参数并更新 SearchContext
   *
   * ⚠️ 注意：这个方法应该在路由变化时调用，由 TrackerV2 内部统一管理
   * 业务代码不需要手动调用，只需要在搜索页面初始化时调用一次即可
   */
  static syncFromCurrentURL(): void {
    if (typeof window === "undefined") return;

    const search = window.location.search;
    if (!search) return;

    // 检查是否是搜索页面
    const pathname = window.location.pathname;
    if (pathname !== "/search") return;

    // 从 URL 参数解析
    const urlContext = this.fromURLParams(search);

    // 如果 URL 中有搜索词，但 SearchContext 不存在，创建它
    const params = new URLSearchParams(search);
    const q = params.get("q");

    // ⚠️ 重要：在检查 SearchContext 是否存在之前，先获取当前的 SearchContext
    // 这样可以确保在创建新的 SearchContext 时，能够保留已有的字段（如 search_result_number）
    const currentContext = this.get();

    if (q && !currentContext) {
      // 如果 SearchContext 不存在，创建它（只传入 search_word）
      // ⚠️ 注意：这里不传入其他字段，因为它们是业务代码设置的，不应该从 URL 参数初始化
      this.create({ search_word: q }, "url_param");
    }

    // 如果 SearchContext 已存在，更新 URL 中的字段
    if (currentContext && q) {
      const updates: Partial<SearchContextData> = {};

      // 更新 search_word（如果 URL 中的与 SearchContext 不一致）
      if (currentContext.search_word !== q) {
        updates.search_word = q;
      }

      // 更新 filter_ids（如果 URL 中有）
      const filterIds = params.get("filter_ids");
      if (filterIds) {
        try {
          // 动态导入 filterUrlParams，避免循环依赖
          const filterUrlParams = require("@/utils/filterUrlParams").default;
          const filterIdsMap = filterUrlParams.decode(filterIds);
          const filterIdsArray = Object.values(filterIdsMap).reduce(
            (result: string[], value: unknown) => {
              if (Array.isArray(value)) {
                result.push(...(value as string[]));
              } else if (typeof value === "string") {
                result.push(value);
              }
              return result;
            },
            [] as string[]
          ) as string[];
          if (filterIdsArray.length > 0) {
            updates.filter_ids = filterIdsArray;
          }
        } catch (e) {
          // 如果解析失败，尝试直接分割
          try {
            updates.filter_ids = filterIds.split(",").filter(Boolean);
          } catch (e2) {
            // 忽略错误
          }
        }
      } else {
        // ⚠️ 重要：如果 URL 中没有 filter_ids 参数，应该清空 SearchContext 中的 filter_ids
        // 这样可以确保与旧版逻辑一致（旧版从 URL 读取，URL 中没有就是空数组）
        if (currentContext.filter_ids && currentContext.filter_ids.length > 0) {
          updates.filter_ids = [];
        }
      }

      // 更新 original_search_word（如果 URL 中有）
      const original = params.get("original");
      if (original && currentContext.original_search_word !== original) {
        updates.original_search_word = original;
      }

      // ⚠️ 重要：从 q 和 original 计算 top_label_list 和 top_label_depth（与旧版逻辑一致）
      // 旧版逻辑：parseUrlAndUpdateState 通过 q.replace(original, "").trim() 计算 top_label_list
      const originalSearchWord = original || q;
      if (originalSearchWord && q) {
        const remainingQuery = q.replace(originalSearchWord, "").trim();
        const topLabelList = remainingQuery
          ? remainingQuery.split(" ").filter(Boolean)
          : [];
        const topLabelDepth = topLabelList.length;

        // 只有当计算出的值与当前值不一致时才更新
        if (
          JSON.stringify(currentContext.top_label_list || []) !==
          JSON.stringify(topLabelList)
        ) {
          updates.top_label_list = topLabelList;
        }
        if (currentContext.top_label_depth !== topLabelDepth) {
          updates.top_label_depth = topLabelDepth;
        }
      }

      // 如果 URL 中显式有 top_label_list 和 top_label_depth，优先使用（向后兼容）
      const topLabelListFromURL = params.get("top_label_list");
      if (topLabelListFromURL) {
        const labelList = topLabelListFromURL.split(",").filter(Boolean);
        if (labelList.length > 0) {
          updates.top_label_list = labelList;
        }
      }
      const topLabelDepthFromURL = params.get("top_label_depth");
      if (topLabelDepthFromURL) {
        const depth = parseInt(topLabelDepthFromURL, 10);
        if (!isNaN(depth)) {
          updates.top_label_depth = depth;
        }
      }

      // 如果有更新，应用它们
      if (Object.keys(updates).length > 0) {
        this.update(updates);

        if (process.env.NODE_ENV === "development") {
          console.log("[SearchContext] Synced from URL:", updates);
        }
      }
    }
  }
}
